import naja from 'naja';
import onDomReady from './onDomReady';
import Filters from './components/filters';
//import MapController from './components/mapController';
import AppSearch from './components/app-search';

class Paginator {
	constructor(button, resultsEl, url, page) {
		onDomReady(() => {
			this.button = button;
			this.resultsEl = resultsEl;
			this.url = this.normalizeUrl(url);
			this.page = page;
			this.minPageLoaded = page;
			this.maxPageLoaded = page;
			this.isNextPage = false;
			this.firstLoad = true;
			//this.map = new MapController();

			// Událost kliknutí na tlačítko
			button.addEventListener('click', () => {
				if (this.isNextPage) {
					this.page = this.maxPageLoaded + 1;
					this.loadData({ insertPosition: 'beforeend' })
						.then(() => {
							this.maxPageLoaded = this.page;
							this.updatePrevButton();
							this.renderPagination();
							this.updateLoadMoreButton();
						})
						.catch(() => {});
				}
			});

			// Posluchač události změny filtru
			window.addEventListener('filter:changed', e => {
				this.page = 1;
				this.minPageLoaded = 1;
				this.maxPageLoaded = 1;
				this.isNextPage = false;
				this.loadData({ clear: true });
			});

			this.defaultText = button.getAttribute('data-text');
			this.loadingText = button.getAttribute('data-text-loading');
			this.rangeEl = document.querySelector('#paginator-range');
			this.topButton = document.querySelector('#paginator-top');
			this.prevButton = document.querySelector('#paginator-prev');
			this.pagesEl = document.querySelector('#paginator-pages');
			this.totalPages = null;
			this.filterManager = new Filters();
			this.appSearch = new AppSearch();
			this.appSearch.init();

			if (this.topButton) {
				this.topButton.addEventListener('click', e => {
					e.preventDefault();
					const target =
						document.querySelector('#results') ||
						document.querySelector('#content') ||
						this.resultsEl;
					const top =
						target.getBoundingClientRect().top +
						window.scrollY -
						20;
					window.scrollTo({ top, behavior: 'smooth' });
				});
			}

			if (this.prevButton) {
				this.prevButton.addEventListener('click', e => {
					e.preventDefault();
					if (this.minPageLoaded <= 1) {
						return;
					}
					const prevPage = this.minPageLoaded - 1;
					this.loadData({
						insertPosition: 'afterbegin',
						updateHistory: false,
						pageOverride: prevPage,
					})
						.then(() => {
							this.minPageLoaded = prevPage;
							this.updatePrevButton();
							this.renderPagination();
							this.updateLoadMoreButton();
						})
						.catch(() => {});
				});
			}

			if (this.pagesEl) {
				this.pagesEl.addEventListener('click', e => {
					const target = e.target;
					if (!(target instanceof HTMLElement)) {
						return;
					}
					const btn = target.closest('[data-page]');
					if (!btn) {
						return;
					}
					const nextPage = Number(btn.getAttribute('data-page'));
					if (!Number.isFinite(nextPage) || nextPage < 1) {
						return;
					}
					if (nextPage === this.page) {
						return;
					}
					this.page = nextPage;
					this.minPageLoaded = nextPage;
					this.maxPageLoaded = nextPage;
					this.loadData({ clear: true });
				});
			}

			this.loadData({ clear: true });
		});
	}

	normalizeUrl(url) {
		try {
			const parsed = new URL(url, window.location.origin);
			parsed.searchParams.delete('page');
			return parsed.toString();
		} catch {
			return url;
		}
	}

	// Skryje tlačítko
	hideButton() {
		this.button.style.display = 'none';
	}

	// Zobrazí tlačítko
	showButton() {
		this.button.style.display = 'inline-block';
	}

	// Spustí animaci načítání
	loadingStarted() {
		this.button.textContent = this.loadingText;
		this.button.setAttribute('disabled', 'true');
	}

	// Ukončí animaci načítání
	loadingEnded() {
		this.button.textContent = this.defaultText;
		this.button.removeAttribute('disabled');
	}

	// Načte data
	loadData({
		clear = false,
		manageLoading = true,
		updateHistory = true,
		insertPosition = 'beforeend',
		pageOverride = null,
	} = {}) {
		if (manageLoading) {
			this.loadingStarted();
		}

		const requestPage = pageOverride ?? this.page;

		return this.filterManager
			.dataForQuery()
			.then(data => {
				data.page = requestPage;
				if (this.firstLoad) {
					data.fl = true;
					this.firstLoad = false;
				}

				return naja.makeRequest('GET', this.url, data, {
					history: false,
				});
			})
			.then(r => {
				if (manageLoading) {
					this.loadingEnded();
				}
				this.isNextPage = r.isNextPage;

				// Writing the results
				if (clear) {
					this.resultsEl.innerHTML = r.html;
				} else {
					this.resultsEl.insertAdjacentHTML(insertPosition, r.html);
				}
				window.LazyLoad.update();

				// Updating filters
				this.filterManager.fromServerResponse(r.filters);

				if (Object.prototype.hasOwnProperty.call(r, 'totalPages')) {
					this.totalPages = r.totalPages;
				} else {
					this.totalPages = null;
				}
				this.updateRangeText();
				this.updateTopButton();
				this.updatePrevButton();
				this.renderPagination();
				this.updateLoadMoreButton();

				if (updateHistory) {
					this.updateHistory(r);
				}
			})
			.catch(() => {
				if (manageLoading) {
					this.loadingEnded();
				}
			});
	}

	updateRangeText() {
		if (!this.rangeEl) {
			return;
		}

		if (!this.totalPages || this.totalPages <= 1) {
			this.rangeEl.textContent = '';
			return;
		}

		this.rangeEl.textContent = `Strana ${this.page} z ${this.totalPages}`;
	}

	updateTopButton() {
		if (!this.topButton) {
			return;
		}

		if (this.page > 1) {
			this.topButton.style.display = 'inline-block';
		} else {
			this.topButton.style.display = 'none';
		}
	}

	updateLoadMoreButton() {
		let hasNext = this.isNextPage;
		if (this.totalPages) {
			hasNext = this.maxPageLoaded < this.totalPages;
		}
		this.isNextPage = hasNext;
		if (hasNext) {
			this.showButton();
		} else {
			this.hideButton();
		}
	}

	updatePrevButton() {
		if (!this.prevButton) {
			return;
		}

		if (this.minPageLoaded > 1) {
			this.prevButton.style.display = 'inline-block';
		} else {
			this.prevButton.style.display = 'none';
		}
	}

	renderPagination() {
		if (!this.pagesEl) {
			return;
		}
		if (!this.totalPages) {
			this.pagesEl.innerHTML = '';
			return;
		}

		const current = this.page;
		const total = this.totalPages;
		const pages = [];

		const prevDisabled = current <= 1;
		const nextDisabled = current >= total;

		if (total <= 9) {
			for (let p = 1; p <= total; p++) {
				pages.push(p);
			}
		} else {
			pages.push(1);

			const start = Math.max(2, current - 2);
			const end = Math.min(total - 1, current + 2);

			if (start > 2) {
				pages.push('ellipsis-start');
			}

			for (let p = start; p <= end; p++) {
				pages.push(p);
			}

			if (end < total - 1) {
				pages.push('ellipsis-end');
			}

			pages.push(total);
		}

		const prevBtn = `<button type="button" class="pagination-link pagination-link--arrow${
			prevDisabled ? ' is-disabled' : ''
		}" data-page="${current - 1}"${
			prevDisabled ? ' disabled' : ''
		} aria-label="Předchozí">‹</button>`;
		const nextBtn = `<button type="button" class="pagination-link pagination-link--arrow${
			nextDisabled ? ' is-disabled' : ''
		}" data-page="${current + 1}"${
			nextDisabled ? ' disabled' : ''
		} aria-label="Další">›</button>`;

		this.pagesEl.innerHTML = [
			prevBtn,
			...pages.map(p => {
				if (typeof p === 'string') {
					return '<span class="pagination-ellipsis">…</span>';
				}
				const isActive = p === current;
				const cls = isActive
					? 'pagination-link pagination-link--active'
					: 'pagination-link';
				return `<button type="button" class="${cls}" data-page="${p}">${p}</button>`;
			}),
			nextBtn,
		].join('');
	}

	updateHistory(r) {
		if (r.fragment) {
			window.location.hash = r.fragment;
		} else {
			let hi = window.location.href.indexOf('#');
			if (hi > 0) {
				window.history.pushState(
					{},
					'',
					window.location.href.substring(0, hi),
				);
			}
		}

		const loc = new URL(window.location.href);
		if (this.page === 1) {
			loc.searchParams.delete('page');
		} else {
			loc.searchParams.set('page', this.page);
		}

		//this.map.addMarkers(r.markers);

		window.history.pushState({}, '', loc.toString());
	}
}

// Získání elementů
const settingsEl = document.querySelector('#paginator-settings');
const resultsEl = document.querySelector('#paginator-results');
const resultsButton = document.querySelector('#paginator-button');

if (settingsEl && resultsEl && resultsButton) {
	const settingsJson = settingsEl.textContent.trim();
	const settings = JSON.parse(settingsJson);
	new Paginator(
		resultsButton,
		resultsEl,
		settings.loadMoreUrl,
		settings.page || 1,
	);
}
